import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiClientError } from '@/services/apiClient';
import {
  listSubtitles,
  type SubtitleListEntry,
  type SubtitleProviderName,
  type SubtitleTarget,
} from '@/services/subtitlesApi';
import type { TrackEntry } from '@/types/streaming';

export type ActiveSelection =
  | { kind: 'off' }
  | { kind: 'provider'; lang: string; url: string }
  /** In-manifest HLS rendition, controlled through the hls.js API. */
  | { kind: 'hls'; lang: string; id: number }
  | { kind: 'external'; lang: string; provider: SubtitleProviderName; fileId: string };

/** A subtitle rendition hls.js found inside the stream manifest itself. */
export interface NativeSubTrack {
  id: number;
  /** Normalized ISO 639-1 (see normalizeSubtitleLang). */
  lang: string;
  label: string;
}

interface Props {
  /** What to search subtitles for. Null (anime) hides the search section. */
  target: SubtitleTarget | null;
  /** Provider-supplied subtitle tracks; may be empty. */
  providerTracks: TrackEntry[];
  /** In-manifest renditions from hls.js; may be empty. */
  nativeTracks?: NativeSubTrack[];
  /** Currently-selected language label, or 'off'. Used to mark the active row. */
  activeLang: string | 'off';
  /** Why the last picked entry failed to load (quota = daily limit hit). */
  fileError?: 'quota' | 'generic' | null;
  onSelect: (selection: ActiveSelection) => void;
}

type LoadStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; entries: SubtitleListEntry[]; languages: string[] }
  | { kind: 'unavailable' } // 503 — api key not configured / upstream down
  | { kind: 'error'; message: string };

function browserLang(): string {
  if (typeof navigator === 'undefined') return 'en';
  const tag = navigator.language || 'en';
  return tag.split('-')[0]?.toLowerCase() || 'en';
}

/**
 * Subtitle selection list, rendered inline inside the player settings panel
 * (mirrors QualityPanel). It must NOT open its own positioned dropdown: an
 * earlier floating-CC version was unusable in practice — the stage overlay
 * ended up under the fixed app nav and the settings-panel copy opened its
 * menu below the viewport.
 */
export function SubtitlePicker({
  target, providerTracks, nativeTracks = [], activeLang, fileError, onSelect,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation();
  const [osExpanded, setOsExpanded] = useState(false);
  const [osStatus, setOsStatus] = useState<LoadStatus>({ kind: 'idle' });
  // Cache the query languages at first expand so changes mid-session (e.g.
  // extensions toggling navigator.language) don't refetch unexpectedly.
  const queryLangsRef = useRef<string[] | null>(null);

  // Fetched results are only valid for ONE target. The picker is NOT
  // remounted on episode navigation (Watch re-renders with a new streamRef),
  // so without this reset a viewer who advances an episode with the panel
  // open would see — and could pick — the previous episode's subtitles.
  const targetKey = target == null ? 'none'
    : target.type === 'movie' ? `m:${target.tmdbId}`
    : `t:${target.tmdbId}:${target.season}:${target.episode}`;
  const prevTargetKeyRef = useRef(targetKey);
  useEffect(() => {
    if (prevTargetKeyRef.current === targetKey) return;
    prevTargetKeyRef.current = targetKey;
    setOsStatus({ kind: 'idle' });
    setOsExpanded(false);
  }, [targetKey]);

  function queryLangs(): string[] {
    if (queryLangsRef.current) return queryLangsRef.current;
    // Priority: browser language, then app UI language, then English.
    const langs = [browserLang(), (i18n.language || 'en').split('-')[0].toLowerCase(), 'en']
      .filter((l, i, arr) => l && arr.indexOf(l) === i);
    queryLangsRef.current = langs;
    return langs;
  }

  async function loadExternal(): Promise<void> {
    if (target == null) return;
    setOsStatus({ kind: 'loading' });
    try {
      const res = await listSubtitles(target, queryLangs());
      setOsStatus({ kind: 'ready', entries: res.entries, languages: res.languages });
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 503) {
        setOsStatus({ kind: 'unavailable' });
        return;
      }
      const message = err instanceof Error ? err.message : 'Couldn\'t load';
      setOsStatus({ kind: 'error', message });
    }
  }

  function expandOs(): void {
    setOsExpanded(true);
    if (osStatus.kind === 'idle' || osStatus.kind === 'error') void loadExternal();
  }

  return (
    <div
      data-testid="subtitle-picker"
      aria-label={t('player.subtitles.label')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 240,
        maxHeight: 220,
        overflowY: 'auto',
        color: '#fff',
      }}
    >
      <button
        type="button"
        data-testid="subtitle-row-off"
        onClick={() => onSelect({ kind: 'off' })}
        style={rowStyle(activeLang === 'off')}
      >
        {t('player.subtitles.off')}
      </button>

      {(providerTracks.length > 0 || nativeTracks.length > 0) && (
        <>
          <div style={sectionHeaderStyle} aria-hidden="true">{t('player.subtitles.availableNow')}</div>
          {nativeTracks.map((track) => (
            <button
              key={`native-${track.id}`}
              type="button"
              data-testid={`subtitle-row-native-${track.id}`}
              onClick={() => onSelect({ kind: 'hls', lang: track.lang, id: track.id })}
              style={rowStyle(activeLang === track.lang)}
            >
              {track.lang.toUpperCase()} · {track.label}
            </button>
          ))}
          {providerTracks.map((track) => (
            <button
              key={track.url}
              type="button"
              data-testid={`subtitle-row-provider-${track.lang}`}
              onClick={() => onSelect({ kind: 'provider', lang: track.lang, url: track.url })}
              style={rowStyle(activeLang === track.lang)}
            >
              {track.lang.toUpperCase()} · {track.label}
            </button>
          ))}
        </>
      )}

      {fileError != null && (
        <div data-testid="subtitle-file-error" style={hintStyle} role="alert">
          {fileError === 'quota' ? t('player.subtitles.quotaReached') : t('player.subtitles.fileFailed')}
        </div>
      )}

      {target != null && (
        <>
          <button
            type="button"
            onClick={osExpanded ? () => setOsExpanded(false) : expandOs}
            aria-expanded={osExpanded}
            data-testid="subtitle-os-toggle"
            style={{ ...sectionHeaderStyle, cursor: 'pointer', width: '100%', textAlign: 'left' }}
          >
            {osExpanded ? '▾' : '▸'} {t('player.subtitles.fromOpenSubtitles')}
          </button>
          {osExpanded && (
            <ExternalSection
              status={osStatus}
              onPick={(entry) => onSelect({
                kind: 'external', lang: entry.language, provider: entry.provider, fileId: entry.fileId,
              })}
              onRetry={loadExternal}
              t={t}
            />
          )}
        </>
      )}
    </div>
  );
}

function ExternalSection({
  status, onPick, onRetry, t,
}: {
  status: LoadStatus;
  onPick: (entry: SubtitleListEntry) => void;
  onRetry: () => void;
  t: TFunction;
}): JSX.Element {
  // Group by language, preserving the backend's ranked order (requested
  // languages first). Hooks must run unconditionally, before early returns.
  const groups = useMemo(() => {
    if (status.kind !== 'ready') return [];
    const byLang = new Map<string, SubtitleListEntry[]>();
    for (const e of status.entries) {
      const list = byLang.get(e.language) ?? [];
      list.push(e);
      byLang.set(e.language, list);
    }
    return [...byLang.entries()];
  }, [status]);

  if (status.kind === 'loading') {
    return <div data-testid="subtitle-os-loading" style={hintStyle}>{t('common.loading')}</div>;
  }
  if (status.kind === 'unavailable') {
    return (
      <div data-testid="subtitle-os-unavailable" style={hintStyle}>
        {t('player.subtitles.moreUnavailable')}
      </div>
    );
  }
  if (status.kind === 'error') {
    return (
      <div data-testid="subtitle-os-error" style={hintStyle}>
        {t('player.subtitles.couldntLoad')}{' '}
        <button
          type="button"
          onClick={onRetry}
          style={{ background: 'transparent', color: '#9cf', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
        >
          {t('player.subtitles.retry')}
        </button>
      </div>
    );
  }
  if (status.kind === 'ready') {
    if (status.entries.length === 0) {
      return <div data-testid="subtitle-os-empty" style={hintStyle}>{t('player.subtitles.noResults')}</div>;
    }
    const showHeaders = groups.length > 1;
    return (
      <>
        {groups.map(([lang, entries]) => (
          <div key={lang}>
            {showHeaders && (
              <div style={langHeaderStyle} aria-hidden="true">{lang.toUpperCase()}</div>
            )}
            {entries.map((e) => (
              <button
                key={`${e.provider}:${e.fileId}`}
                type="button"
                data-testid={`subtitle-row-os-${e.fileId}`}
                onClick={() => onPick(e)}
                style={rowStyle(false)}
                title={e.releaseName ?? undefined}
              >
                <span style={ellipsisStyle}>
                  {!showHeaders && `${e.language.toUpperCase()} · `}
                  {e.releaseName ?? e.fileId}
                </span>
                {e.hearingImpaired && <span style={badgeStyle}>{t('player.subtitles.hearingImpaired')}</span>}
                {e.aiTranslated && <span style={badgeStyle}>{t('player.subtitles.aiTranslated')}</span>}
              </button>
            ))}
          </div>
        ))}
      </>
    );
  }
  return <></>;
}

const sectionHeaderStyle: React.CSSProperties = {
  padding: '6px 10px 2px',
  fontSize: '0.75rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.6)',
  background: 'transparent',
  border: 'none',
  font: 'inherit',
};

const langHeaderStyle: React.CSSProperties = {
  padding: '6px 10px 2px',
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  color: 'rgba(255,255,255,0.5)',
};

const hintStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: '0.85rem',
  color: 'rgba(255,255,255,0.7)',
};

const ellipsisStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

const badgeStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: '0.65rem',
  padding: '1px 4px',
  borderRadius: 3,
  border: '1px solid rgba(255,255,255,0.35)',
  color: 'rgba(255,255,255,0.7)',
};

function rowStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    background: 'transparent',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    font: 'inherit',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
    textAlign: 'left',
  };
}
